import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Film, Eye, CheckCircle2, Clock, XCircle, Tv, Wallet, ShieldOff, Captions, Tv2, Trash2, Pencil } from "lucide-react";
import CaptionManager from "../components/CaptionManager";
import EditMovieModal from "../components/EditMovieModal";

function SeriesManager() {
  const [list, setList] = useState([]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [selGenres, setSelGenres] = useState([]);
  const [genreCatalog, setGenreCatalog] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/series?mine=true").then((r) => setList(r.data || [])).catch(() => setList([]));
  useEffect(() => {
    load();
    api.get("/genres").then(({ data }) => {
      const list = data || [];
      setGenreCatalog(list);
      const def = list.find((g) => g.name === "Drama") || list[0];
      if (def) setSelGenres([def.name]);
    }).catch(() => setGenreCatalog([]));
  }, []);

  const toggleGenre = (n) => {
    setSelGenres((prev) => {
      if (prev.includes(n)) return prev.filter((g) => g !== n);
      if (prev.length >= 3) { toast.error("Up to 3 genres per series."); return prev; }
      return [...prev, n];
    });
  };

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (selGenres.length === 0) return toast.error("Pick at least one genre.");
    setBusy(true);
    try {
      await api.post("/series", { title: name.trim(), description: desc.trim(), genre: selGenres[0], genres: selGenres });
      toast.success("Series created. You can now attach episodes when uploading.");
      setName(""); setDesc(""); setAdding(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this series? Episodes will be detached but kept.")) return;
    try { await api.delete(`/series/${id}`); toast.success("Series deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <section className="mb-12" data-testid="series-manager">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-bold flex items-center gap-2">
          <Tv2 className="h-5 w-5 text-[#E65F2B]" /> Your series
        </h2>
        <button onClick={() => setAdding(!adding)} className="ghost-btn" data-testid="series-toggle-add">
          <Plus className="h-4 w-4" /> {adding ? "Cancel" : "New series"}
        </button>
      </div>

      {adding && (
        <form onSubmit={create} className="card-surface p-5 mb-4 space-y-3" data-testid="series-create-form">
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Series title (e.g. 'Acts of Faith')"
            className="input-dark"
            maxLength={200}
            data-testid="series-title-input"
          />
          <textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            rows={2}
            placeholder="Short description (optional)"
            className="input-dark resize-none"
            maxLength={2000}
            data-testid="series-description-input"
          />
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-eyebrow">Genres · pick up to 3</span>
              <span className="text-xs text-white/40">{selGenres.length} / 3</span>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="series-genre-chips">
              {genreCatalog.map((g) => {
                const active = selGenres.includes(g.name);
                const capped = !active && selGenres.length >= 3;
                return (
                  <button
                    type="button"
                    key={g.id}
                    onClick={() => toggleGenre(g.name)}
                    disabled={capped}
                    className={`px-3 py-1.5 rounded-full border text-xs transition ${
                      active
                        ? "bg-[#E65F2B] border-[#E65F2B] text-white"
                        : capped
                        ? "border-white/5 text-white/20 cursor-not-allowed"
                        : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"
                    }`}
                    data-testid={`series-genre-chip-${g.slug}`}
                  >
                    {g.name}
                  </button>
                );
              })}
            </div>
          </div>
          <button type="submit" disabled={busy || !name.trim()} className="ember-btn disabled:opacity-40" data-testid="series-create-submit">
            {busy ? "Creating..." : "Create series"}
          </button>
        </form>
      )}

      {list.length === 0 ? (
        <div className="card-surface p-6 text-center text-white/40 text-sm" data-testid="series-empty">
          No series yet. Group multiple episodes under one show, e.g. a documentary series or a kids' Bible show.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="series-list">
          {list.map((s) => (
            <div key={s.id} className="card-surface p-4 flex items-center gap-3" data-testid={`series-row-${s.id}`}>
              <div className="h-10 w-10 rounded-lg bg-[#E65F2B]/15 border border-[#E65F2B]/30 flex items-center justify-center shrink-0">
                <Tv2 className="h-4 w-4 text-[#E65F2B]" />
              </div>
              <div className="flex-1 min-w-0">
                <Link to={`/series/${s.id}`} className="font-display font-bold hover:text-[#E65F2B]">{s.title}</Link>
                <p className="text-xs text-white/40 mt-0.5">
                  {s.genre} · {s.episode_count} episode{s.episode_count === 1 ? "" : "s"}
                </p>
              </div>
              <button
                onClick={() => remove(s.id)}
                className="ghost-btn !py-1 !px-3 text-xs hover:border-red-500/50 hover:text-red-400"
                data-testid={`series-delete-${s.id}`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function CreatorDashboard() {
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [movies, setMovies] = useState([]);
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [captioning, setCaptioning] = useState(null);
  const [editing, setEditing] = useState(null); // movie being edited

  const refresh = async () => {
    const [c, m] = await Promise.all([
      api.get("/channels/me").then((r) => r.data).catch(() => null),
      api.get("/movies/mine").then((r) => r.data).catch(() => []),
    ]);
    setChannel(c);
    setMovies(m || []);
    setLoading(false);
  };
  const load = refresh;

  useEffect(() => { refresh(); }, []);

  const createChannel = async (e) => {
    e.preventDefault();
    setCreatingChannel(true);
    try {
      await api.post("/channels", { name, description: desc });
      toast.success("Channel created!");
      setName(""); setDesc("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setCreatingChannel(false);
    }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-[#E65F2B] border-t-transparent animate-spin" /></div>;

  const stats = {
    total: movies.length,
    approved: movies.filter((m) => m.status === "approved").length,
    pending: movies.filter((m) => m.status === "pending").length,
    rejected: movies.filter((m) => m.status === "rejected").length,
    views: movies.reduce((s, m) => s + (m.views || 0), 0),
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12" data-testid="creator-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
        <div>
          <p className="text-eyebrow">Creator Studio</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black mt-2 tracking-tighter">
            {channel ? <>Welcome, <span className="text-[#E65F2B]">{channel.name}</span></> : "Set up your channel"}
          </h1>
        </div>
        {channel && (
          <div className="flex flex-wrap gap-3">
            <Link to="/creator/revenue" className="ghost-btn" data-testid="view-revenue-button">
              <Wallet className="h-4 w-4" /> Revenue
            </Link>
            <Link to="/creator/appeals" className="ghost-btn" data-testid="view-appeals-button">
              <ShieldOff className="h-4 w-4" /> Appeals
            </Link>
            <Link to="/creator/upload" className="ember-btn" data-testid="upload-new-movie-button">
              <Plus className="h-4 w-4" /> Upload New Movie
            </Link>
          </div>
        )}
      </div>

      {!channel ? (
        <form onSubmit={createChannel} className="card-surface p-8 max-w-xl" data-testid="create-channel-form">
          <Tv className="h-8 w-8 text-[#E65F2B] mb-4" />
          <h2 className="font-display text-2xl font-bold mb-2">Name your channel</h2>
          <p className="text-sm text-white/50 mb-6">This becomes your filmmaker identity on Christoreel. Choose carefully — viewers will discover your titles under this name.</p>
          <div className="space-y-4">
            <div>
              <label className="text-eyebrow mb-2 block">Channel Name</label>
              <input required minLength={2} value={name} onChange={(e) => setName(e.target.value)} className="input-dark" placeholder="e.g. Light & Reel Studios" data-testid="channel-name-input" />
            </div>
            <div>
              <label className="text-eyebrow mb-2 block">Description</label>
              <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} className="input-dark resize-none" placeholder="What kinds of stories will you tell?" data-testid="channel-desc-input" />
            </div>
            <button type="submit" disabled={creatingChannel} className="ember-btn w-full justify-center" data-testid="create-channel-button">
              {creatingChannel ? "Creating..." : "Open my channel"}
            </button>
          </div>
        </form>
      ) : (
        <>
          {/* Stats grid */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
            {[
              { label: "Total titles", value: stats.total, icon: Film, color: "#9CA3AF" },
              { label: "Approved", value: stats.approved, icon: CheckCircle2, color: "#22C55E" },
              { label: "Pending", value: stats.pending, icon: Clock, color: "#EAB308" },
              { label: "Rejected", value: stats.rejected, icon: XCircle, color: "#EF4444" },
              { label: "Total views", value: stats.views, icon: Eye, color: "#E65F2B" },
            ].map((s) => (
              <div key={s.label} className="card-surface p-5">
                <s.icon className="h-5 w-5 mb-3" style={{ color: s.color }} />
                <p className="font-display text-3xl font-black">{s.value}</p>
                <p className="text-xs text-white/50 mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          <SeriesManager />

          <h2 className="font-display text-2xl font-bold mb-6">Your library</h2>
          {(() => {
            const incomplete = movies.filter((m) => m.status === "pending_upload" || m.status === "processing");
            if (incomplete.length === 0) return null;
            return (
              <div className="mb-8" data-testid="incomplete-uploads-section">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-block h-2 w-2 rounded-full bg-[#E65F2B] animate-pulse" />
                  <h3 className="text-eyebrow text-[#E65F2B]">Incomplete uploads</h3>
                </div>
                <div className="space-y-3">
                  {incomplete.map((m) => (
                    <div key={m.id} className="card-surface p-5 border-[#E65F2B]/30 flex items-center gap-4" data-testid={`incomplete-${m.id}`}>
                      <div className="w-16 h-24 rounded-md bg-black overflow-hidden shrink-0">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/media/poster/${m.id}`} alt="" className="h-full w-full object-cover" onError={(e) => e.currentTarget.style.display = "none"} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-display font-bold truncate">{m.title}</h3>
                          <span className="badge-pill text-[10px] bg-[#E65F2B]/15 text-[#E65F2B] border border-[#E65F2B]/30">
                            {m.status === "pending_upload" ? "needs resume" : "transcoding"}
                          </span>
                        </div>
                        <p className="text-xs text-white/50 mt-1">{m.genre} · started {new Date(m.created_at).toLocaleString()}</p>
                      </div>
                      {m.status === "pending_upload" ? (
                        <Link to={`/creator/resume/${m.id}`} className="ember-btn !py-2 !px-4 text-sm" data-testid={`resume-button-${m.id}`}>
                          Resume upload
                        </Link>
                      ) : (
                        <span className="text-xs text-white/40">Cloudflare is processing — refresh in a moment</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          {movies.length === 0 ? (
            <div className="card-surface p-12 text-center" data-testid="no-movies">
              <Film className="h-12 w-12 mx-auto text-white/20 mb-4" />
              <p className="text-white/60">No titles yet. Upload your first movie to get started.</p>
              <Link to="/creator/upload" className="ember-btn mt-6 inline-flex"><Plus className="h-4 w-4" /> Upload first movie</Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="my-movies-list">
              {movies.filter((m) => m.status !== "pending_upload" && m.status !== "processing").map((m) => (
                <div key={m.id} className="card-surface p-5 flex items-center gap-4">
                  <div className="w-16 h-24 rounded-md bg-black overflow-hidden shrink-0">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/media/poster/${m.id}`} alt="" className="h-full w-full object-cover" onError={(e) => e.currentTarget.style.display = "none"} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-display font-bold truncate">{m.title}</h3>
                      <span className={`badge-pill text-[10px] ${m.status === "approved" ? "bg-green-500/10 text-green-400 border border-green-500/30" : m.status === "pending" ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/30" : "bg-red-500/10 text-red-400 border border-red-500/30"}`}>
                        {m.status}
                      </span>
                    </div>
                    <p className="text-xs text-white/50 mt-1">{m.genre} · {m.views} views · uploaded {new Date(m.created_at).toLocaleDateString()}</p>
                    {m.rejection_reason && <p className="text-xs text-red-400 mt-1">Reason: {m.rejection_reason}</p>}
                  </div>
                  <Link to={`/movie/${m.id}`} className="ghost-btn !py-2 !px-4 text-sm">View</Link>
                  <button
                    onClick={() => setEditing(m)}
                    className="ghost-btn !py-2 !px-4 text-sm hover:border-[#E65F2B]/60 hover:text-[#E65F2B]"
                    data-testid={`edit-movie-${m.id}`}
                    title="Edit title, description, genres or poster"
                  >
                    <Pencil className="h-4 w-4" /> Edit
                  </button>
                  {m.status === "approved" && (
                    <button
                      onClick={() => setCaptioning(m)}
                      className="ghost-btn !py-2 !px-4 text-sm"
                      data-testid={`captions-button-${m.id}`}
                      title="Manage subtitles"
                    >
                      <Captions className="h-4 w-4" /> CC
                      {m.captions?.length ? <span className="ml-1 text-[#E65F2B]">·{m.captions.length}</span> : null}
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      const confirmText = m.status === "approved"
                        ? `Take down "${m.title}"? This immediately hides the film from Christoreel and can't be undone. Viewers who saved progress will lose it.`
                        : `Delete "${m.title}"? This can't be undone.`;
                      if (!window.confirm(confirmText)) return;
                      try {
                        await api.delete(`/movies/${m.id}`);
                        toast.success(`"${m.title}" taken down.`);
                        load();
                      } catch (e) { toast.error(e.response?.data?.detail || "Couldn't take down."); }
                    }}
                    className="ghost-btn !py-2 !px-4 text-sm hover:border-red-500/50 hover:text-red-400"
                    data-testid={`takedown-movie-${m.id}`}
                    title={m.status === "approved" ? "Take this film down from Christoreel" : "Delete this draft"}
                  >
                    <Trash2 className="h-4 w-4" /> {m.status === "approved" ? "Take down" : "Delete"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {captioning && (
        <CaptionManager movie={captioning} onClose={() => { setCaptioning(null); load(); }} />
      )}
      {editing && (
        <EditMovieModal movie={editing} onClose={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}
